import React, { useEffect, useState } from 'react';
import AppLayout from '../../components/layout/AppLayout';
import { PageHeader } from '../../components/common/PageHeader';
import api from '../../lib/api';
import { toast } from 'sonner';
import { SPECIALIZATIONS, SERVICE_TYPES, AVAILABILITY_STATUSES } from '../../lib/constants';
import { Save, X } from 'lucide-react';
import LanguagePairsInput from '../../components/common/LanguagePairsInput';

const MultiSelect = ({ options, selected, onChange, placeholder, testid }) => (
  <div data-testid={testid}>
    <div className="flex flex-wrap gap-1.5 mb-2">
      {selected.map(s => (
        <span key={s} className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-xs px-2 py-1 rounded">
          {s}
          <button type="button" onClick={() => onChange(selected.filter(x => x !== s))} className="hover:text-red-600"><X className="w-3 h-3" /></button>
        </span>
      ))}
    </div>
    <select value="" onChange={(e) => { if (e.target.value && !selected.includes(e.target.value)) onChange([...selected, e.target.value]); }} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none">
      <option value="">{placeholder}</option>
      {options.filter(o => !selected.includes(o.value ?? o)).map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
    </select>
  </div>
);

const InterpreterProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/interpreters/me').then(r => setProfile(r.data)).catch(() => toast.error('Profil konnte nicht geladen werden'));
  }, []);

  if (!profile) return <AppLayout><div>Lade...</div></AppLayout>;

  const upd = (k, v) => setProfile(p => ({ ...p, [k]: v }));

  const save = async () => {
    setLoading(true);
    try {
      const data = {
        salutation: profile.salutation || '',
        first_name: profile.first_name, last_name: profile.last_name,
        phone: profile.phone,
        street: profile.street, house_number: profile.house_number,
        postal_code: profile.postal_code, city: profile.city, country: profile.country,
        languages: profile.languages, target_languages: profile.target_languages,
        language_pairs: profile.language_pairs || [],
        specializations: profile.specializations, service_types: profile.service_types,
        availability_status: profile.availability_status, custom_rate: parseFloat(profile.custom_rate) || 0,
        sworn: !!profile.sworn,
        notes: profile.notes,
      };
      const res = await api.put('/interpreters/me', data);
      setProfile(res.data);
      toast.success('Profil gespeichert');
    } catch (err) {
      toast.error('Fehler beim Speichern');
    } finally { setLoading(false); }
  };

  return (
    <AppLayout>
      <PageHeader title="Mein Profil" description="Persönliche Daten, Sprachkombinationen, Fachgebiete und Verfügbarkeit" />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <section className="bg-white border border-slate-200 rounded-md card-subtle p-6">
            <h3 className="font-heading text-base font-medium text-slate-900 mb-4">Persönliche Daten</h3>
            <div className="grid grid-cols-[130px_1fr_1fr] gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Anrede</label>
                <select value={profile.salutation || ''} onChange={(e) => upd('salutation', e.target.value)} data-testid="profile-salutation" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none">
                  <option value="">—</option>
                  <option value="Herr">Herr</option>
                  <option value="Frau">Frau</option>
                  <option value="Divers">Divers</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Vorname</label>
                <input value={profile.first_name} onChange={(e) => upd('first_name', e.target.value)} data-testid="profile-firstname" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Nachname</label>
                <input value={profile.last_name} onChange={(e) => upd('last_name', e.target.value)} data-testid="profile-lastname" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">E-Mail</label>
                <input value={profile.email} disabled className="w-full border border-slate-200 bg-slate-50 rounded-md px-3 py-2.5 text-sm text-slate-500" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Telefon</label>
                <input value={profile.phone || ''} onChange={(e) => upd('phone', e.target.value)} data-testid="profile-phone" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
              <div className="col-span-2">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Adresse (für Honorar-Abrechnung)</label>
                <div className="grid grid-cols-[1fr_100px] gap-2 mb-2">
                  <input value={profile.street || ''} onChange={(e) => upd('street', e.target.value)} placeholder="Straße" data-testid="profile-street" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                  <input value={profile.house_number || ''} onChange={(e) => upd('house_number', e.target.value)} placeholder="Hausnr." data-testid="profile-housenr" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                </div>
                <div className="grid grid-cols-[120px_1fr] gap-2">
                  <input value={profile.postal_code || ''} onChange={(e) => upd('postal_code', e.target.value)} placeholder="PLZ" data-testid="profile-plz" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                  <input value={profile.city || ''} onChange={(e) => upd('city', e.target.value)} placeholder="Ort" data-testid="profile-city" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
                </div>
              </div>
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-md card-subtle p-6">
            <h3 className="font-heading text-base font-medium text-slate-900 mb-4">Sprachen & Fachgebiete</h3>
            <div className="mb-4">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Sprachpaare (bidirektional ↔)</label>
              <LanguagePairsInput
                value={profile.language_pairs || []}
                onChange={(pairs) => upd('language_pairs', pairs)}
                testidPrefix="profile-pairs"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Fachgebiete</label>
                <MultiSelect options={SPECIALIZATIONS} selected={profile.specializations || []} onChange={(v) => upd('specializations', v)} placeholder="Wählen..." testid="profile-specializations" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Einsatzarten</label>
                <MultiSelect options={SERVICE_TYPES} selected={profile.service_types || []} onChange={(v) => upd('service_types', v)} placeholder="Wählen..." testid="profile-service-types" />
              </div>
            </div>
          </section>

          <section className="bg-white border border-slate-200 rounded-md card-subtle p-6">
            <h3 className="font-heading text-base font-medium text-slate-900 mb-4">Qualifikation</h3>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={!!profile.sworn} onChange={(e) => upd('sworn', e.target.checked)} data-testid="profile-sworn" className="w-4 h-4 mt-0.5" />
              <span>
                <span className="text-sm font-medium text-slate-800">Allgemein beeidigt / vereidigt</span>
                <span className="block text-xs text-slate-500 mt-0.5">Nur aktivieren, wenn Sie bei einem deutschen Gericht allgemein beeidigt oder ermächtigt sind. Diese Angabe erhöht Ihre Sichtbarkeit bei juristischen und behördlichen Aufträgen.</span>
              </span>
            </label>
          </section>

          <section className="bg-white border border-slate-200 rounded-md card-subtle p-6">
            <h3 className="font-heading text-base font-medium text-slate-900 mb-4">Notizen</h3>
            <textarea rows="3" value={profile.notes || ''} onChange={(e) => upd('notes', e.target.value)} data-testid="profile-notes" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" placeholder="Qualifikationen, Besonderheiten..." />
          </section>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-slate-200 rounded-md card-subtle p-6 sticky top-6">
            <h3 className="font-heading text-base font-medium text-slate-900 mb-4">Verfügbarkeit & Honorar</h3>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Status</label>
                <div className="space-y-2">
                  {AVAILABILITY_STATUSES.map(a => (
                    <button
                      key={a.value}
                      type="button"
                      onClick={() => upd('availability_status', a.value)}
                      data-testid={`availability-${a.value}`}
                      className={`w-full text-left text-sm px-3 py-2 rounded-md border transition-colors ${profile.availability_status === a.value ? a.color + ' ring-1 ring-current' : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">Honorar (€/Std)</label>
                <input type="number" step="0.01" value={profile.custom_rate} onChange={(e) => upd('custom_rate', e.target.value)} data-testid="profile-rate" className="w-full border border-slate-300 rounded-md px-3 py-2.5 text-sm font-mono focus:ring-2 focus:ring-[#0F4C81] focus:border-transparent outline-none" />
              </div>
              <button onClick={save} disabled={loading} data-testid="profile-save-btn" className="w-full bg-[#0F4C81] hover:bg-[#0A365C] text-white font-medium rounded-md px-4 py-2.5 text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
                <Save className="w-4 h-4" /> {loading ? 'Speichern...' : 'Profil speichern'}
              </button>
              {!profile.active_status && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  Ihr Konto ist noch nicht freigeschaltet. Sie können Aufträge erst annehmen, sobald ein Admin Sie aktiviert.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default InterpreterProfile;
